import { SelectField, SelectOptionType } from "@/components";

import { DatePickerField, InputField } from "@/components";
import { PAGINATION } from "@/constants/common";
import {
  DATA_SOURCE_TYPE,
  optionsDataSource,
  optionsRepeat,
} from "@/partial/campaign/constant";
import { CampaignForm } from "@/partial/campaign/types";
import { useUploadList } from "@/services/hooks/upload/useUpload";
import useCampaignStore from "@/store/campaignStore";
import { formatDateTime } from "@/utils/common";
import { useEffect, useMemo } from "react";
import {
  Control,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from "react-hook-form";
import { Card } from "tvs-unified-lib";
import {
  CardDetail,
  LayoutLeft,
  StatusText,
  SubTextDetail,
  TextDetail,
} from "../../styles";
import { RepeatForm } from "../repeat-form";

interface LeftLayoutFormProps {
  isEdit: boolean;
  control: Control<CampaignForm>;
  getValues: UseFormGetValues<CampaignForm>;
  watch: UseFormWatch<CampaignForm>;
  setValue: UseFormSetValue<CampaignForm>;
  errors: FieldErrors<CampaignForm>;
  trigger: UseFormTrigger<CampaignForm>;
}

export const LeftLayoutForm = ({
  isEdit,
  control,
  getValues,
  setValue,
  errors,
  watch,
  trigger,
}: LeftLayoutFormProps) => {
  const {
    campaignDetail: campaignDetailStore,
    optionsDataFile: optionsDataFileStore,
  } = useCampaignStore();

  const checkAIDC = watch("data_source") && watch("data_source")[0]?.value;
  const startDate = watch("start_date");

  const { data: uploadList } = useUploadList({
    limit: PAGINATION.MAX_LIMIT,
    page: PAGINATION.MIN_PAGE,
    status: "success",
  });

  const optionDataFile = useMemo(
    () =>
      uploadList?.response.length &&
      uploadList?.response.map((item: any) => {
        return {
          label: item.fileName,
          value: item.id,
          other: {
            excelHeaders: item.excelHeaders,
            headerMapping: item.headerMapping,
            user_field_mapping: item.userFieldMapping,
          },
        };
      }),
    [uploadList?.response]
  );

  useEffect(() => {
    if (
      campaignDetailStore?.dataSource === DATA_SOURCE_TYPE.DATA_UPLOAD &&
      campaignDetailStore?.uploadFile?.id &&
      optionsDataFileStore?.length
    ) {
      const foundOption = optionsDataFileStore.find(
        (i: SelectOptionType) => i.value === campaignDetailStore.uploadFile.id
      );
      if (foundOption) {
        setValue("data_file", [foundOption]);
      }
    }
  }, [campaignDetailStore, setValue, optionsDataFileStore]);

  return (
    <LayoutLeft>
      <Card heading="Details" className="card-detail">
        <CardDetail>
          <TextDetail>Status</TextDetail>
          <StatusText>{getValues("status")}</StatusText>
        </CardDetail>
        <CardDetail>
          <TextDetail>Campaign ID</TextDetail>
          <SubTextDetail>
            {isEdit ? getValues("campaign_id") : "-"}
          </SubTextDetail>
        </CardDetail>
        <CardDetail>
          <TextDetail>Created By</TextDetail>
          <SubTextDetail>{isEdit ? getValues("createdBy") : "-"}</SubTextDetail>
        </CardDetail>
        <CardDetail>
          <TextDetail>Created Date</TextDetail>
          <SubTextDetail>
            {isEdit ? formatDateTime(getValues("createdAt") || "-") : "-"}
          </SubTextDetail>
        </CardDetail>
      </Card>
      <Card heading="Campaign Information" className="card-information">
        <InputField
          name="name"
          label="Campaign Name"
          control={control}
          placeholder="Enter Campaign Name"
          isRequired
        />
        <SelectField
          name="data_source"
          label="Data Source"
          placeholder="Select Data Source"
          control={control}
          options={optionsDataSource}
          isRequired
          disabled={isEdit}
        />
        {checkAIDC === "Data Upload" && (
          <SelectField
            name="data_file"
            label="Data File"
            control={control}
            options={isEdit ? optionsDataFileStore : optionDataFile} // TODO: add options from api backend
            isRequired={checkAIDC === "Data Upload"}
            disabled={isEdit}
          />
        )}
        <DatePickerField
          control={control}
          name="start_date"
          label="Start date"
          isRequired
          minDate={new Date()}
          placeholder="Select Start Date"
          disabled={isEdit}
        />
        <DatePickerField
          control={control}
          name="end_date"
          label="End date"
          placeholder="Select End Date"
          disabled={!startDate || isEdit}
          isRequired
          minDate={startDate ? new Date(startDate) : new Date()}
        />
        <SelectField
          name="repeat"
          label="Repeat"
          isRequired
          control={control}
          options={optionsRepeat}
          keepOpen
          dropdownHeight="600px"
          itemRenderer={(e) => {
            if (!e.other) return <>{e.label}</>;
            return (
              <RepeatForm
                control={control}
                setValue={setValue}
                getValues={getValues}
                errors={errors}
                trigger={trigger}
                watch={watch}
              />
            );
          }}
        />
      </Card>
    </LayoutLeft>
  );
};

export default LeftLayoutForm;
